import React, { useState, useEffect } from 'react';
import { Box, Modal, Typography, Grid, Card, CardContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaletteIcon from '@mui/icons-material/Palette';
import PsychologyIcon from '@mui/icons-material/Psychology';
import LPManagement from './LPManagement';
import StrategyManagement from './StrategyManagement';
import { useAuth } from '../../provider/authProvider';

const SettingsMain = ({ open, onClose, selectedAccount }) => {
    const [currentView, setCurrentView] = useState('main');
    const [selectedSection, setSelectedSection] = useState(null);
    const { hasPermission } = useAuth();

    // Reset to main view when modal opens
    useEffect(() => {
        if (open) {
            setCurrentView('main');
            setSelectedSection(null);
        }
    }, [open]);

    const settingsCategories = [
        {
            id: 'lp-management',
            title: 'LP Management',
            description: 'Configure liquidity providers and connections',
            icon: <AccountBalanceIcon sx={{ fontSize: 40, color: '#00cfff' }} />,
            color: '#00cfff'
        },
        {
            id: 'security',
            title: 'Security',
            description: 'Authentication and access control',
            icon: <SecurityIcon sx={{ fontSize: 40, color: '#1de9b6' }} />,
            color: '#1de9b6'
        },
        {
            id: 'data',
            title: 'Data Management',
            description: 'Data storage and backup settings',
            icon: <StorageIcon sx={{ fontSize: 40, color: '#ff6b6b' }} />,
            color: '#ff6b6b'
        },
        {
            id: 'notifications',
            title: 'Notifications',
            description: 'Alert and notification preferences',
            icon: <NotificationsIcon sx={{ fontSize: 40, color: '#ffd600' }} />,
            color: '#ffd600'
        },
        {
            id: 'appearance',
            title: 'Appearance',
            description: 'Theme and display settings',
            icon: <PaletteIcon sx={{ fontSize: 40, color: '#7b61ff' }} />,
            color: '#7b61ff'
        },
        {
            id: 'strategies',
            title: 'Strategies',
            description: 'Trading strategies and configurations',
            icon: <PsychologyIcon sx={{ fontSize: 40, color: '#ff9800' }} />,
            color: '#ff9800'
        }
    ];

    const handleCategoryClick = (category) => {
        setSelectedSection(category);
        setCurrentView(category.id);
    };

    const handleBack = () => {
        setCurrentView('main');
        setSelectedSection(null);
    };

    const renderMainView = () => (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                    Settings
                </Typography>
                <IconButton onClick={onClose} sx={{ color: '#fff' }}>
                    <CloseIcon />
                </IconButton>
            </Box>
            
            <Grid container spacing={3}>
                {settingsCategories.map((category) => {
                    // Check permissions for each category
                    let hasAccess = false;
                    switch (category.id) {
                        case 'lp-management':
                            hasAccess = hasPermission('Settings.LPManagement');
                            break;
                        case 'security':
                            hasAccess = hasPermission('Settings.Security');
                            break;
                        case 'data':
                            hasAccess = hasPermission('Settings.DataManagement');
                            break;
                        case 'notifications':
                        case 'appearance':
                            hasAccess = hasPermission('Settings.NotificationAppearance');
                            break;
                        case 'strategies':
                            hasAccess = hasPermission('Settings.Strategies');
                            break;
                        default:
                            hasAccess = true; // Allow access to other categories
                    }
                    
                    if (!hasAccess) return null;
                    
                    return (
                    <Grid item xs={12} sm={6} md={4} key={category.id}>
                        <Card 
                            sx={{ 
                                background: 'rgba(24, 31, 44, 0.8)',
                                border: '1px solid rgba(6, 182, 212, 0.2)',
                                borderRadius: 2,
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    borderColor: category.color,
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 8px 25px rgba(0, 0, 0, 0.3)`
                                }
                            }}
                            onClick={() => handleCategoryClick(category)}
                        >
                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                <Box sx={{ mb: 2 }}>
                                    {category.icon}
                                </Box>
                                <Typography variant="h6" sx={{ color: '#fff', mb: 1, fontWeight: 600 }}>
                                    {category.title}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#bfc9db', fontSize: '0.875rem' }}>
                                    {category.description}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    );
                })}
            </Grid>
        </Box>
    );

    const renderSectionView = () => {
        switch (currentView) {
            case 'lp-management':
                if (!hasPermission('Settings.LPManagement')) {
                    return renderMainView();
                }
                return <LPManagement onBack={handleBack} onClose={handleBack} />;
            case 'strategies':
                if (!hasPermission('Settings.Strategies')) {
                    return renderMainView();
                }
                return <StrategyManagement onBack={handleBack} onClose={handleBack} selectedAccount={selectedAccount} />;
            default:
                return renderMainView();
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
            }}
        >
            <Box sx={{
                width: '90vw',
                maxWidth: 1200,
                height: '90vh',
                maxHeight: 800,
                background: 'linear-gradient(135deg, #1f2937 0%, #374151 50%, #1e3a8a 100%)',
                borderRadius: 2,
                border: '1px solid rgba(6, 182, 212, 0.3)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8)',
                overflow: 'hidden'
            }}>
                {currentView === 'main' ? renderMainView() : renderSectionView()}
            </Box>
        </Modal>
    );
};

export default SettingsMain; 